// #region imports
import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import PrimaryStorageButtonGroup from './PrimaryStorageButtonGroup';
import { PasswordField } from './PasswordField';
import { ReactComponent as Logo } from '../../../core/assets/logos/enigma.svg';
import useLogin from '../../hooks/useLogin';
import { useTranslation } from 'react-i18next';
// #endregion

const Login = () => {
  const { storageSelected, formik, changePrimaryStorage } = useLogin();
  const { t } = useTranslation();

  // #region header
  const loginHeader = (
    <Stack spacing="6">
      <Logo style={{ height: '80px' }} />
      <Stack spacing={{ base: '2', md: '3' }} textAlign="center">
        <Heading size={{ base: 'xs', md: 'sm' }}>
          {t('login.loginToAccount')}
        </Heading>
        <HStack spacing="1" justify="center">
          <Text color="fg.muted">{t('login.noAccount')}</Text>
          <Button variant="text" colorScheme="blue">
            {t('login.signup')}
          </Button>
        </HStack>
      </Stack>
    </Stack>
  );
  //   #endregion


  // #region full name 
  const fullNameView = (
    <FormControl isInvalid={!!formik.errors.fullname}>
      <FormLabel htmlFor="fullname">{t('login.fullname')}</FormLabel>
      <Input
        id="fullname"
        type="string"
        value={formik.values.fullname}
        onChange={formik.handleChange}
      />
      {formik.errors.fullname && (
        <FormErrorMessage>{formik.errors.fullname}</FormErrorMessage>
      )}
    </FormControl>
  )
  // #endregion

  // #region email
  const emailView = (
    <FormControl isInvalid={!!formik.errors.email}>
      <FormLabel htmlFor="email">{t('login.email')}</FormLabel>
      <Input
        id="email"
        type="email"
        value={formik.values.email}
        onChange={formik.handleChange}
      />
      {formik.errors.email && (
        <FormErrorMessage>{formik.errors.email}</FormErrorMessage>
      )}
    </FormControl>
  );
  //   #endregion

  // #region password
  const passView = (
    <PasswordField
      title={t('login.password')}
      value={formik.values.password}
      onChange={formik.handleChange}
      name="password"
      error={formik.errors.password}
    />
  );

  //   #endregion

  // #region forgot pass view
  const forgotPassView = (
    <HStack justify="space-between">
      <Checkbox defaultChecked>{t('login.rememberMe')}</Checkbox>
      <Button variant="text" colorScheme="blue" size="sm">
        {t('login.forgotPassword')}
      </Button>
    </HStack>
  );
  // #endregion

  // #region primary Storage
  const primaryStorage = !storageSelected && (
    <>
      <HStack>
        <Divider />
        <Text fontSize="sm" whiteSpace="nowrap" color="fg.muted">
          {t('login.primaryStorage')}
        </Text>
        <Divider />
      </HStack>
      <PrimaryStorageButtonGroup
        storage={formik.values.primaryStorage}
        changePrimaryStorage={changePrimaryStorage}
      />
    </>
  );
  // #endregion

  // #region container
  const container = (
    <Container
      maxW="lg"
      py={{ base: '12', md: '24' }}
      px={{ base: '0', sm: '8' }}
    >
      <Stack spacing="8">
        {loginHeader}
        <Box
          py={{ base: '0', sm: '8' }}
          px={{ base: '4', sm: '10' }}
          bg={{ base: 'transparent', sm: 'bg.surface' }}
          boxShadow={{ base: 'none', sm: 'md' }}
          borderRadius={{ base: 'none', sm: 'xl' }}
        >
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing="6">
              <Stack spacing="5">
                {fullNameView}
                {emailView}
                {passView}
              </Stack>

              {forgotPassView}
              <Stack spacing="6">
                <Button colorScheme="blue" type="submit">
                  {t('login.signin')}
                </Button>
                {primaryStorage}
              </Stack>
            </Stack>
          </form>
        </Box>
      </Stack>
    </Container>
  );
  //   #endregion

  return container;
};
export default Login;
